import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";

export function InfoCard({ icon: Icon, label, value, isDark }) {
  return (
    <div
      className={`rounded-[2rem] border p-5 shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>{label}</p>
          <p className={`mt-3 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
            {value || "Not available"}
          </p>
        </div>
        <div className="rounded-2xl bg-sky-100 p-3 text-sky-700">
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
}

export function SectionCard({ title, description, children, isDark }) {
  return (
    <section
      className={`rounded-[2rem] border p-6 shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-5">
        <h2 className={`text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{title}</h2>
        <p className={`mt-2 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>{description}</p>
      </div>
      {children}
    </section>
  );
}

export function EmptyState({ title, body, isDark }) {
  return (
    <div
      className={`rounded-3xl border p-5 text-sm ${
        isDark ? "border-slate-800 bg-slate-950 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"
      }`}
    >
      <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{title}</p>
      <p className="mt-2 leading-6">{body}</p>
    </div>
  );
}

export function SummaryTile({ label, value, isDark }) {
  return (
    <div
      className={`rounded-2xl border px-4 py-3 ${
        isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
      }`}
    >
      <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>{label}</p>
      <p className={`mt-2 text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

export function formatMoney(amount, currency) {
  const numericAmount = Number(amount || 0);
  return `${currency} ${numericAmount.toLocaleString()}`;
}

export function formatDate(value) {
  if (!value) return "Not available";
  return value;
}

export function parseSupportingDocuments(value) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => {
          if (typeof item === "string") {
            return { label: item.split("/").pop() || "Document", reference: item };
          }

          if (item && typeof item === "object") {
            return {
              label: item.label || item.name || item.reference?.split("/").pop() || "Document",
              reference: item.reference || item.url || "",
            };
          }

          return null;
        })
        .filter(Boolean)
        .filter((item) => item.reference);
    }
  } catch (_error) {
    return String(value)
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => ({ label: item.split("/").pop() || "Document", reference: item }));
  }

  return [];
}

export function getStatusClasses(status) {
  if (status === "Paid" || status === "Completed") return "bg-emerald-100 text-emerald-700";
  if (status === "Pending" || status === "Open") return "bg-sky-100 text-sky-700";
  if (status === "In progress") return "bg-indigo-100 text-indigo-700";
  return "bg-amber-100 text-amber-800";
}

function extractOrganizationId(user) {
  return (
    user?.app_metadata?.organization_id ||
    user?.app_metadata?.organizationId ||
    user?.user_metadata?.organization_id ||
    user?.user_metadata?.organizationId ||
    null
  );
}

export function useTenantPortalData(user, toast) {
  const [tenant, setTenant] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentIntents, setPaymentIntents] = useState([]);
  const [maintenanceTickets, setMaintenanceTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIssue, setSavingIssue] = useState(false);
  const [payingInvoiceId, setPayingInvoiceId] = useState("");
  const [issueForm, setIssueForm] = useState({
    issue: "",
    category: "Tenant report",
    priority: "Medium",
  });

  useEffect(() => {
    let isMounted = true;

    const loadPortal = async () => {
      if (!user || !supabase) {
        if (isMounted) {
          setTenant(null);
          setInvoices([]);
          setPayments([]);
          setPaymentIntents([]);
          setMaintenanceTickets([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      const tenantId = user.app_metadata?.tenant_id || user.user_metadata?.tenant_id || null;
      const organizationId = extractOrganizationId(user);
      let tenantQuery = supabase.from("tenants").select("*").limit(1);

      if (organizationId) {
        tenantQuery = tenantQuery.eq("organization_id", organizationId);
      }

      if (tenantId) {
        tenantQuery = tenantQuery.eq("id", tenantId);
      } else {
        tenantQuery = tenantQuery.eq("email", user.email);
      }

      const { data: tenantData, error: tenantError } = await tenantQuery.maybeSingle();

      if (!isMounted) return;

      if (tenantError) {
        console.error("Error loading tenant profile:", tenantError);
        setTenant(null);
        setInvoices([]);
        setPayments([]);
        setPaymentIntents([]);
        setMaintenanceTickets([]);
        setLoading(false);
        return;
      }

      const linkedTenant = tenantData || null;
      setTenant(linkedTenant);

      if (!linkedTenant) {
        setInvoices([]);
        setPayments([]);
        setPaymentIntents([]);
        setMaintenanceTickets([]);
        setLoading(false);
        return;
      }

      let tenantInvoiceQuery = supabase
        .from("invoices")
        .select("id, tenant, property, unit, amount, amount_paid, balance_due, issue_date, due_date, status, delivery, external_reference, organization_id")
        .eq("tenant", linkedTenant.full_name)
        .eq("property", linkedTenant.property_name)
        .eq("unit", linkedTenant.unit)
        .order("created_at", { ascending: false });

      if (organizationId) {
        tenantInvoiceQuery = tenantInvoiceQuery.eq("organization_id", organizationId);
      }

      let paymentsQuery = supabase
        .from("payments")
        .select("id, invoice_id, amount, due_date, status, payment_method, notes, tenant_id")
        .eq("tenant_id", linkedTenant.id)
        .order("created_at", { ascending: false });

      let paymentIntentsQuery = supabase
        .from("payment_intents")
        .select("id, tenant_id, invoice_id, amount, currency, phone_number, external_reference, status, created_at, updated_at")
        .eq("tenant_id", linkedTenant.id)
        .order("created_at", { ascending: false });

      let maintenanceQuery = supabase
        .from("maintenance_tickets")
        .select("id, issue, property, unit, category, priority, status, assigned, reported_on, supporting_files")
        .eq("property", linkedTenant.property_name)
        .eq("unit", linkedTenant.unit)
        .eq("category", "Tenant report")
        .order("created_at", { ascending: false });

      if (organizationId) {
        paymentsQuery = paymentsQuery.eq("organization_id", organizationId);
        paymentIntentsQuery = paymentIntentsQuery.eq("organization_id", organizationId);
        maintenanceQuery = maintenanceQuery.eq("organization_id", organizationId);
      }

      let { data: invoiceData, error: invoicesError } = await tenantInvoiceQuery;

      if (!invoicesError && (invoiceData?.length || 0) === 0) {
        let fallbackInvoiceQuery = supabase
          .from("invoices")
          .select("id, tenant, property, unit, amount, amount_paid, balance_due, issue_date, due_date, status, delivery, external_reference, organization_id")
          .eq("tenant", linkedTenant.full_name)
          .order("created_at", { ascending: false });

        if (organizationId) {
          fallbackInvoiceQuery = fallbackInvoiceQuery.eq("organization_id", organizationId);
        }

        const fallbackInvoiceResult = await fallbackInvoiceQuery;
        invoiceData = (fallbackInvoiceResult.data || []).filter(
          (invoice) =>
            String(invoice?.property || "") === String(linkedTenant.property_name || "") &&
            String(invoice?.unit || "") === String(linkedTenant.unit || "")
        );
        invoicesError = fallbackInvoiceResult.error;
      }

      const [
        { data: paymentData, error: paymentsError },
        { data: paymentIntentData, error: paymentIntentsError },
        { data: maintenanceData, error: maintenanceError },
      ] = await Promise.all([paymentsQuery, paymentIntentsQuery, maintenanceQuery]);

      if (!isMounted) return;

      if (invoicesError) {
        console.error("Error loading tenant invoices:", invoicesError);
        setInvoices([]);
      } else {
        setInvoices(invoiceData || []);
      }

      if (paymentsError) {
        console.error("Error loading tenant payments:", paymentsError);
        setPayments([]);
      } else {
        setPayments(paymentData || []);
      }

      if (paymentIntentsError) {
        console.error("Error loading tenant payment intents:", paymentIntentsError);
        setPaymentIntents([]);
      } else {
        setPaymentIntents(paymentIntentData || []);
      }

      if (maintenanceError) {
        console.error("Error loading tenant maintenance tickets:", maintenanceError);
        setMaintenanceTickets([]);
      } else {
        setMaintenanceTickets(maintenanceData || []);
      }

      setLoading(false);
    };

    loadPortal();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const leaseEndText = useMemo(() => formatDate(tenant?.lease_end), [tenant?.lease_end]);
  const documents = useMemo(() => parseSupportingDocuments(tenant?.supporting_documents), [tenant?.supporting_documents]);

  const paymentSummary = useMemo(() => {
    const paid = payments
      .filter((payment) => payment.status === "Paid")
      .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const pendingPayments = payments
      .filter((payment) => payment.status !== "Paid")
      .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const linkedPaymentInvoiceIds = new Set(
      payments
        .filter((payment) => String(payment.status || "").toLowerCase() !== "paid")
        .map((payment) => payment.invoice_id)
        .filter(Boolean)
    );
    const pendingIntents = paymentIntents.filter((intent) => {
      const normalizedStatus = String(intent.status || "").toLowerCase();
      return ["pending", "submitted"].includes(normalizedStatus) && !linkedPaymentInvoiceIds.has(intent.invoice_id);
    });
    const pendingIntentAmount = pendingIntents.reduce(
      (sum, intent) => sum + Number(intent.amount || 0),
      0
    );

    return {
      total: payments.length + pendingIntents.length,
      paid,
      pending: pendingPayments + pendingIntentAmount,
    };
  }, [payments, paymentIntents]);

  const invoiceSummary = useMemo(() => {
    const total = invoices.reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0);
    const outstanding = invoices
      .filter((invoice) => invoice.status !== "Paid")
      .reduce((sum, invoice) => sum + Number((invoice.balance_due ?? invoice.amount) || 0), 0);
    const overdue = invoices
      .filter((invoice) => invoice.status === "Overdue")
      .reduce((sum, invoice) => sum + Number((invoice.balance_due ?? invoice.amount) || 0), 0);

    return {
      count: invoices.length,
      total,
      outstanding,
      overdue,
    };
  }, [invoices]);

  const paymentIntentLookup = useMemo(() => {
    const lookup = new Map();

    for (const intent of paymentIntents) {
      const invoiceId = intent?.invoice_id;

      if (!invoiceId || lookup.has(invoiceId)) {
        continue;
      }

      lookup.set(invoiceId, intent);
    }

    return lookup;
  }, [paymentIntents]);

  const handlePayNow = async (invoice) => {
    if (!supabase || !tenant) {
      toast?.error("Payment request unavailable", "Tenant payment context could not be resolved.");
      return;
    }

    const outstandingAmount = Number(invoice?.balance_due ?? invoice?.amount ?? 0);

    if (!Number.isFinite(outstandingAmount) || outstandingAmount <= 0) {
      toast?.info("No balance due", "This invoice no longer has an outstanding balance.");
      return;
    }

    if (!tenant.organization_id) {
      toast?.error("Organization missing", "This tenant record is missing organization context.");
      return;
    }

    if (!tenant.phone) {
      toast?.error("Phone number required", "Add the tenant phone number before sending a payment request.");
      return;
    }

    const existingIntent = paymentIntentLookup.get(invoice.id);
    if (existingIntent && ["pending", "submitted"].includes(String(existingIntent.status || "").toLowerCase())) {
      toast?.info("Payment already requested", "A payment request is already pending for this invoice.");
      return;
    }

    setPayingInvoiceId(invoice.id);

    const { data, error } = await supabase.functions.invoke("create-payment-intent", {
      body: {
        organizationId: tenant.organization_id,
        invoiceId: invoice.id,
        tenantId: tenant.id,
        amount: outstandingAmount,
        currency: "KES",
        phoneNumber: tenant.phone,
      },
    });

    setPayingInvoiceId("");

    if (error) {
      console.error("Error creating payment intent:", error);
      toast?.error("Payment request failed", error.message || "Please try again in a moment.");
      return;
    }

    const nextIntent = {
      id: data?.paymentIntentId,
      tenant_id: data?.tenantId || tenant.id,
      invoice_id: data?.invoiceId || invoice.id,
      amount: data?.amount ?? outstandingAmount,
      currency: data?.currency || "KES",
      phone_number: data?.phoneNumber || tenant.phone,
      external_reference: data?.externalReference || null,
      status: data?.status || "pending",
      created_at: data?.createdAt || new Date().toISOString(),
      updated_at: data?.createdAt || new Date().toISOString(),
    };

    setPaymentIntents((current) => [
      nextIntent,
      ...current.filter((intent) => intent.invoice_id !== invoice.id),
    ]);

    toast?.success(
      "Payment request created",
      "Your invoice is now marked as pending payment. The M-Pesa prompt will be enabled once live provider credentials are added."
    );
  };

  const handleIssueSubmit = async (event) => {
    event.preventDefault();

    if (!tenant?.property_name || !tenant?.unit) {
      toast?.error(
        "Tenant record incomplete",
        "This tenant is missing a property or unit assignment, so the maintenance request could not be created."
      );
      return;
    }

    if (!issueForm.issue.trim()) {
      toast?.info("Describe the issue", "Please enter the maintenance issue before submitting.");
      return;
    }

    setSavingIssue(true);

    const payload = {
      issue: issueForm.issue.trim(),
      property: tenant.property_name,
      unit: tenant.unit,
      category: "Tenant report",
      priority: issueForm.priority,
      status: "Open",
      assigned: "Resident portal",
      reported_on: new Date().toISOString().slice(0, 10),
      supporting_files: null,
      organization_id: tenant.organization_id || null,
    };

    const { data, error } = await supabase
      .from("maintenance_tickets")
      .insert([payload])
      .select("*")
      .single();

    setSavingIssue(false);

    if (error) {
      console.error("Error creating tenant maintenance request:", error);
      toast?.error("Issue could not be submitted", "Please try again in a moment.");
      return;
    }

    setMaintenanceTickets((current) => [data, ...current]);
    setIssueForm({
      issue: "",
      category: "Tenant report",
      priority: "Medium",
    });
    toast?.success("Issue submitted", "Your maintenance request has been added successfully.");
  };

  return {
    tenant,
    invoices,
    payments,
    paymentIntents,
    maintenanceTickets,
    loading,
    savingIssue,
    payingInvoiceId,
    issueForm,
    setIssueForm,
    handleIssueSubmit,
    handlePayNow,
    leaseEndText,
    documents,
    paymentSummary,
    invoiceSummary,
    paymentIntentLookup,
  };
}
